import { colors, type, vizRamp } from "../../styles";
import { formatNumber } from "../../format";

export interface FunnelStep {
  label: string;
  value: number;
}

/**
 * Horizontal funnel: one blue ordinal ramp (never a categorical rainbow — the
 * steps are ordered stages of one thing, not separate entities), with the
 * step-to-step conversion labeled between rows.
 *
 * Pure UI with no data source of its own, so it renders whatever it is handed —
 * on this pass it is only ever handed placeholder framing, never numbers.
 */
const FunnelBars = ({ steps }: { steps: FunnelStep[] }) => {
  const max = Math.max(...steps.map(step => step.value), 1);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {steps.map((step, index) => {
        const previous = index > 0 ? steps[index - 1].value : undefined;
        const conversion =
          previous && previous > 0 ? (step.value / previous) * 100 : undefined;

        return (
          <div key={step.label}>
            {conversion !== undefined && (
              <div
                style={{
                  ...type.bodySmallRegular,
                  color: colors.inkFaint,
                  padding: "3px 0 3px 12px",
                }}
              >
                ↓ {conversion.toFixed(1)}%
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  ...type.bodySmallRegular,
                  color: colors.inkMuted,
                  width: 150,
                  flexShrink: 0,
                }}
                title={step.label}
              >
                {step.label}
              </div>
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <div
                  style={{
                    height: 22,
                    width: `${Math.max((step.value / max) * 100, 1)}%`,
                    background: vizRamp[Math.min(index, vizRamp.length - 1)],
                    borderRadius: 4,
                  }}
                />
                <span
                  style={{
                    ...type.bodySmallMedium,
                    color: colors.ink,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatNumber(step.value)}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FunnelBars;
